import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTenant } from "@/hooks/useTenant";
import {
  Users,
  Building2,
  Mail,
  Loader2,
  ChevronRight,
  ChevronLeft,
  X,
  Plus,
  Check,
} from "lucide-react";

const JURISDICTIONS = [
  { value: "us", label: "United States" },
  { value: "sg", label: "Singapore" },
  { value: "hk", label: "Hong Kong" },
  { value: "uk", label: "United Kingdom" },
  { value: "ch", label: "Switzerland" },
  { value: "ae", label: "UAE" },
  { value: "au", label: "Australia" },
  { value: "jp", label: "Japan" },
  { value: "other", label: "Other" },
];

const FAMILY_ROLES = [
  { value: "FamilyAdmin", label: "Family Admin", description: "Full access to family data and settings" },
  { value: "FamilyManager", label: "Family Manager", description: "Can manage family data, no settings access" },
  { value: "FamilyViewer", label: "Family Viewer", description: "Read-only access to family data" },
];

// Step 1 schema
const step1Schema = z.object({
  name: z.string().min(1, "Family name is required").max(100),
  jurisdiction: z.string().min(1, "Please select a jurisdiction"),
});

// Step 2 schema
const step2Schema = z.object({
  portal_access_enabled: z.boolean(),
});

// Combined schema for form
const familySchema = step1Schema.merge(step2Schema);
type FamilyFormData = z.infer<typeof familySchema>;

interface InviteEntry {
  id: string;
  email: string;
  role: string;
}

interface CreateFamilyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function CreateFamilyDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateFamilyDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { orgId } = useTenant();
  const [step, setStep] = useState(1);
  const [invites, setInvites] = useState<InviteEntry[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("FamilyViewer");

  const form = useForm<FamilyFormData>({
    resolver: zodResolver(familySchema),
    defaultValues: {
      name: "",
      jurisdiction: "",
      portal_access_enabled: false,
    },
  });

  const createFamilyMutation = useMutation({
    mutationFn: async (data: FamilyFormData & { invites: InviteEntry[] }) => {
      if (!orgId) throw new Error("No organization found");

      // Create the family
      const { data: family, error: familyError } = await supabase
        .from("families")
        .insert({
          org_id: orgId,
          name: data.name,
          meta: { jurisdiction: data.jurisdiction },
          portal_access_enabled: data.portal_access_enabled,
          onboarding_status: "draft",
        })
        .select()
        .single();

      if (familyError) throw familyError;

      // Create invitations if any
      if (data.invites.length > 0 && data.portal_access_enabled) {
        const { data: { user } } = await supabase.auth.getUser();

        const invitationRecords = data.invites.map((invite) => ({
          org_id: orgId,
          family_id: family.id,
          email: invite.email,
          role: invite.role,
          created_by: user?.id,
        }));

        const { error: inviteError } = await supabase
          .from("family_invitations")
          .insert(invitationRecords);

        if (inviteError) {
          console.error("Error creating invitations:", inviteError);
          // Don't fail the whole operation, just log
        } else {
          // Get created invitations and send emails
          const { data: createdInvites } = await supabase
            .from("family_invitations")
            .select("id")
            .eq("family_id", family.id);

          // Trigger edge function for each invitation
          if (createdInvites) {
            for (const invite of createdInvites) {
              supabase.functions.invoke("send-family-invitation", {
                body: { invitation_id: invite.id },
              }).catch((err) => console.error("Failed to send invitation email:", err));
            }
          }
        }
      }

      return family;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["families"] });
      queryClient.invalidateQueries({ queryKey: ["firm-metrics"] });
      toast({
        title: "Family created",
        description: "The new family has been created successfully.",
      });
      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create family",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    form.reset();
    setStep(1);
    setInvites([]);
    setNewEmail("");
    setNewRole("FamilyViewer");
    onOpenChange(false);
  };

  const handleNext = async () => {
    if (step === 1) {
      const isValid = await form.trigger(["name", "jurisdiction"]);
      if (isValid) setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleAddInvite = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (invites.some((i) => i.email.toLowerCase() === newEmail.toLowerCase())) {
      toast({
        title: "Duplicate email",
        description: "This email has already been added",
        variant: "destructive",
      });
      return;
    }

    setInvites([
      ...invites,
      { id: crypto.randomUUID(), email: newEmail, role: newRole },
    ]);
    setNewEmail("");
  };

  const handleRemoveInvite = (id: string) => {
    setInvites(invites.filter((i) => i.id !== id));
  };

  const handleSubmit = () => {
    const formData = form.getValues();
    createFamilyMutation.mutate({ ...formData, invites });
  };

  const portalEnabled = form.watch("portal_access_enabled");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Add New Family
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Enter basic information about the family"}
            {step === 2 && "Configure portal access settings"}
            {step === 3 && "Invite family members (optional)"}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 py-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`h-2 flex-1 rounded-full ${
                  s <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            </div>
          ))}
        </div>

        <Form {...form}>
          <form className="space-y-4">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Family Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., The Smith Family"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="jurisdiction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Jurisdiction *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select jurisdiction" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {JURISDICTIONS.map((j) => (
                            <SelectItem key={j.value} value={j.value}>
                              {j.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Where the family is primarily based
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Step 2: Portal Access */}
            {step === 2 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="portal_access_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Enable Portal Access
                        </FormLabel>
                        <FormDescription>
                          Allow family members to log in to Vintage to view
                          their portfolio and documents
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {!portalEnabled && (
                  <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Portal access is disabled. Your team will manage this
                    family's data on their behalf.
                  </div>
                )}

                {portalEnabled && (
                  <div className="rounded-lg bg-blue-50 border-blue-200 border p-4 text-sm">
                    <Mail className="h-4 w-4 inline mr-2 text-blue-600" />
                    You can invite family members in the next step.
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Invite Users */}
            {step === 3 && (
              <div className="space-y-4">
                {!portalEnabled ? (
                  <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground text-center py-8">
                    Portal access is disabled for this family.
                    <br />
                    No invitations needed.
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@example.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddInvite();
                          }
                        }}
                        className="flex-1"
                      />
                      <Select value={newRole} onValueChange={setNewRole}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FAMILY_ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleAddInvite}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {invites.length > 0 ? (
                      <div className="space-y-2">
                        {invites.map((invite) => (
                          <div
                            key={invite.id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div className="flex items-center gap-3">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{invite.email}</span>
                              <Badge variant="outline" className="text-xs">
                                {FAMILY_ROLES.find((r) => r.value === invite.role)?.label}
                              </Badge>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveInvite(invite.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-sm text-muted-foreground">
                        No invitations added yet.
                        <br />
                        You can skip this step and invite users later.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              )}

              {step < 3 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={createFamilyMutation.isPending}
                >
                  {createFamilyMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Create Family
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
